import { RoiCalculation } from "../../../graphql/types";
import { RoiResultsData } from "../models/RoiResults";
import type { RoiCalculationDataParsed } from "../hooks/useRoiCalculationData";
import { parseAwsJson, readNumber } from "./roiStageData";

interface RoiResultPayload {
  economics?: {
    currency?: string;
    feedCostBaseline?: number;
    feedCostIntervention?: number;
    climateChangeBaseline?: number;
    climateChangeIntervention?: number;
    revenueModelAvailable?: boolean;
  };
  kpis?: {
    feedCost?: { absolute?: number; percentage?: number | null };
    footprintChange?: { absolute?: number; percentage?: number | null };
    roi?: {
      absolute?: number;
      percentage?: number | null;
      netCost?: number;
      withCarbonAbsolute?: number;
      withCarbonPercentage?: number | null;
    };
    ghgAbated?: number;
    costOfAbatement?: number;
  };
}

const num = (raw: unknown): number => readNumber(raw) ?? 0;

// "Nutritional" is the interventionCategories value that represents feed
// changes (SSTLL-6570 AC3) the results screen only shows
// Cost of Abatement for feed interventions.
export const FEED_INTERVENTION_CATEGORY = "Nutritional";

const parseResult = (raw: unknown): RoiResultPayload =>
  parseAwsJson<RoiResultPayload>(raw) ?? {};

const climateFromParsed = (
  parsedData: RoiCalculationDataParsed | null,
  side: "baseline" | "intervention"
): number | undefined => {
  const scenario =
    side === "baseline" ? parsedData?.baseline : parsedData?.intervention;
  const air = scenario?.impactSummary?.air as
    | Record<string, unknown>
    | undefined;
  return readNumber(air?.value);
};

const climateUnitFromParsed = (
  parsedData: RoiCalculationDataParsed | null
): string | undefined => {
  const air = parsedData?.baseline?.impactSummary?.air as
    | { unit?: unknown }
    | undefined;
  return typeof air?.unit === "string" ? air.unit : undefined;
};

interface MapRoiCalculationArgs {
  calculation: Pick<RoiCalculation, "result"> | null;
  parsedData: RoiCalculationDataParsed | null;
  baselineName: string;
  interventionName: string;
  fallbackCurrency: string;
}

// eslint-disable-next-line import/prefer-default-export
export const mapRoiCalculationToResults = ({
  calculation,
  parsedData,
  baselineName,
  interventionName,
  fallbackCurrency,
}: MapRoiCalculationArgs): RoiResultsData => {
  const payload = parseResult(calculation?.result);
  const economics = payload.economics ?? {};
  const kpis = payload.kpis ?? {};
  const roi = kpis.roi ?? {};

  const climateBaselineValue =
    readNumber(economics.climateChangeBaseline) ??
    climateFromParsed(parsedData, "baseline");
  const climateInterventionValue =
    readNumber(economics.climateChangeIntervention) ??
    climateFromParsed(parsedData, "intervention");
  const climateBaseline = climateBaselineValue ?? 0;
  const climateIntervention = climateInterventionValue ?? 0;
  const climateAbsolute =
    readNumber(kpis.footprintChange?.absolute) ?? climateBaseline - climateIntervention;
  const climatePercentage =
    readNumber(kpis.footprintChange?.percentage) ??
    (climateBaseline === 0
      ? 0
      : ((climateBaseline - climateIntervention) / climateBaseline) * 100);

  const withCarbonAbsolute = readNumber(roi.withCarbonAbsolute);
  const costOfAbatement = readNumber(kpis.costOfAbatement);
  const ghgAbated = readNumber(kpis.ghgAbated);
  // Absent on older persisted snapshots (pre-dating this field) — treated as
  // available, matching their actual behavior (broiler/pig/turkey only,
  // before broiler_breeder/laying_hen/sow existed).
  const revenueModelAvailable = economics.revenueModelAvailable ?? true;

  const stages =
    (parsedData?.intervention?.stages || parsedData?.baseline?.stages) ?? [];
  const fallbackStage = stages[stages.length - 1];
  const stageName =
    parsedData?.intervention?.defaultStageName ??
    parsedData?.baseline?.defaultStageName ??
    fallbackStage?.stageName ??
    "";
  const outputType =
    parsedData?.intervention?.defaultStageType ??
    parsedData?.baseline?.defaultStageType ??
    fallbackStage?.stageType ??
    "";

  return {
    baselineName,
    interventionName,
    currency: economics.currency ?? fallbackCurrency,
    totalFeedCost: {
      baseline: num(economics.feedCostBaseline),
      intervention: num(economics.feedCostIntervention),
      absoluteChange: num(kpis.feedCost?.absolute),
      percentageChange: num(kpis.feedCost?.percentage),
    },
    totalClimateChange: {
      baseline: climateBaseline,
      intervention: climateIntervention,
      absoluteChange: climateAbsolute,
      percentageChange: climatePercentage,
      unit: climateUnitFromParsed(parsedData) ?? "",
      stageName,
      outputType,
      available:
        climateBaselineValue !== undefined ||
        climateInterventionValue !== undefined,
    },
    roiExcludingCarbon: {
      value: num(roi.absolute),
      percentage: num(roi.percentage),
    },
    roiIncludingCarbon: {
      value: withCarbonAbsolute ?? num(roi.absolute),
      percentage: num(roi.withCarbonPercentage),
      available: withCarbonAbsolute !== undefined,
    },
    costOfAbatement: {
      value: costOfAbatement ?? 0,
      available: costOfAbatement !== undefined,
      footprintAvailable: ghgAbated !== undefined,
    },
    isFeedIntervention: parsedData?.intervention?.category === FEED_INTERVENTION_CATEGORY,
    revenueModelAvailable,
  };
};
